import { useRef, useState } from 'react';
import { formatNumber, spriteUrl } from './format';
import { workers } from '../game/data';
import { getWorkerPrice, getWorkerCps, getAvailableUpgrades } from '../game/rules';

// A worker shows in the store once you own one or have come close to
// affording it; the next one after that shows as a locked silhouette.
function isWorkerRevealed(worker, counters) {
  return counters[worker.type] > 0 || counters.earned >= worker.price * 0.5;
}

function Sprite({ icon, locked = false, className = '' }) {
  return (
    <img
      src={spriteUrl(icon)}
      alt=""
      draggable={false}
      className={`object-contain ${locked ? 'brightness-0 opacity-40' : ''} ${className}`} />
  );
}

export function Store({
  state,
  onBuyWorker,
  onBuyUpgrade
}) {
  return (
    <aside className="flex flex-col bg-oxford-blue text-platinum lg:w-96 lg:h-screen lg:sticky lg:top-0 lg:overflow-y-auto">
      <h2 className="px-4 pt-4 pb-2 text-2xl font-bold text-white">Store</h2>
      <UpgradeShelf state={state} onBuy={onBuyUpgrade} />
      <WorkerList state={state} onBuy={onBuyWorker} />
    </aside>
  );
}

function UpgradeShelf({ state, onBuy }) {
  const available = getAvailableUpgrades(state);
  // The tile whose tooltip is showing, and where to draw the tooltip below it.
  const [hovered, setHovered] = useState(null);
  const lastPointer = useRef('mouse');

  const show = (id, element) => setHovered({ id, top: element.offsetTop + element.offsetHeight + 6 });
  const shown = hovered && available.find(u => u.id === hovered.id);

  // On touch screens the first tap previews the tooltip and the second tap buys.
  const onTileClick = (upgrade, element) => {
    if (lastPointer.current === 'touch' && hovered?.id !== upgrade.id) {
      show(upgrade.id, element);
      return;
    }
    onBuy(upgrade.id);
  };

  return (
    <section className="px-4 pb-4 border-b border-white/10">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-ash-gray mb-2">Upgrades</h3>
      {available.length === 0 ? (
        <p className="text-sm text-ash-gray italic">Keep coding to unlock upgrades.</p>
      ) : (
        <ul className="relative flex flex-wrap gap-2" onMouseLeave={() => setHovered(null)}>
          {available.map(upgrade => {
            const canBuy = state.counters.lines >= upgrade.price;
            return (
              <li key={upgrade.id} className="motion-safe:animate-pop-in">
                <button
                  onPointerDown={(e) => { lastPointer.current = e.pointerType; }}
                  onClick={(e) => onTileClick(upgrade, e.currentTarget)}
                  onMouseEnter={(e) => show(upgrade.id, e.currentTarget)}
                  onFocus={(e) => show(upgrade.id, e.currentTarget)}
                  onBlur={() => setHovered(null)}
                  aria-label={`${upgrade.name}, ${formatNumber(upgrade.price)} lines. ${upgrade.effect}`}
                  className={`relative w-14 h-14 p-2 rounded-md border-2 bg-platinum transition
                    ${canBuy ? 'border-avocado hover:bg-white cursor-pointer' : 'border-transparent opacity-50'}`}>
                  <UpgradeIcon upgrade={upgrade} />
                </button>
              </li>
            );
          })}
          {shown && (
            <li className="absolute inset-x-0 z-20 pointer-events-none" style={{ top: hovered.top }}>
              <UpgradeTooltip upgrade={shown} canBuy={state.counters.lines >= shown.price} />
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

function UpgradeIcon({ upgrade }) {
  const [main, second] = upgrade.icons;
  return (
    <>
      <Sprite icon={main} className="w-full h-full" />
      {second && (
        <span className="absolute -bottom-1 -right-1 w-6 h-6 p-0.5 rounded bg-white shadow">
          <Sprite icon={second} className="w-full h-full" />
        </span>
      )}
      {upgrade.tier && (
        <span className="absolute -bottom-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-oxford-blue text-[10px] font-bold leading-5 text-white">
          {upgrade.tier}
        </span>
      )}
      {upgrade.kind === 'promotion' && (
        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-avocado text-[10px] font-bold leading-4 text-white">↑</span>
      )}
    </>
  );
}

function UpgradeTooltip({ upgrade, canBuy }) {
  return (
    <div className="rounded-md bg-white text-oxford-blue p-3 text-sm shadow-xl motion-safe:animate-pop-in">
      <div className="flex items-baseline justify-between gap-2">
        <p className="font-semibold">{upgrade.name}</p>
        <p className={`shrink-0 font-semibold ${canBuy ? 'text-avocado' : 'text-red-pantone'}`}>
          {formatNumber(upgrade.price)} lines
        </p>
      </div>
      <p className="text-xs text-oxford-blue/60">{upgrade.subtitle}</p>
      <p className="mt-1 text-xs">{upgrade.description}</p>
      <p className="mt-1 text-xs font-semibold">{upgrade.effect}</p>
    </div>
  );
}

function WorkerList({ state, onBuy }) {
  const { counters } = state;
  const firstLocked = workers.findIndex(worker => !isWorkerRevealed(worker, counters));
  const visible = firstLocked === -1 ? workers : workers.slice(0, firstLocked + 1);

  return (
    <section className="py-2">
      <h3 className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-ash-gray">Workers</h3>
      <ul>
        {visible.map((worker, index) => (
          <WorkerRow
            // Remount when a worker unlocks so it pops in.
            key={`${worker.type}:${index === firstLocked}`}
            worker={worker}
            locked={index === firstLocked}
            state={state}
            onBuy={() => onBuy(worker.type)} />
        ))}
      </ul>
    </section>
  );
}

function WorkerRow({
  worker,
  locked,
  state,
  onBuy
}) {
  const owned = state.counters[worker.type];
  const price = getWorkerPrice(worker, owned);
  const canBuy = !locked && state.counters.lines >= price;
  const each = getWorkerCps(state, worker);

  return (
    <li className="motion-safe:animate-pop-in">
      <button
        onClick={onBuy}
        disabled={!canBuy}
        title={locked ? undefined : `${worker.description} ${owned} owned, ${formatNumber(owned * each)} lines/s total.`}
        className={`w-full flex items-center gap-3 px-4 py-2 text-left transition
          ${canBuy ? 'hover:bg-white/10 cursor-pointer' : 'opacity-60 cursor-not-allowed'}`}>
        <div className="w-12 h-12 shrink-0 p-1.5 rounded-md bg-platinum">
          <Sprite icon={worker.icon} locked={locked} className="w-full h-full" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white truncate">{locked ? '???' : worker.name}</p>
          <p className={`text-sm ${canBuy ? 'text-avocado' : 'text-ash-gray'}`}>
            {formatNumber(price)} lines
            {!locked && <span className="text-ash-gray"> · +{formatNumber(each)}/s each</span>}
          </p>
        </div>
        {!locked && <span className="text-3xl font-bold text-white/30 tabular-nums">{owned}</span>}
      </button>
    </li>
  );
}
